import React, { useEffect, useState, useMemo } from "react";
import {
  FaEdit,
  FaTrash,
  FaUserPlus,
  FaSearch,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaWallet,
} from "react-icons/fa";
import moment from "moment";
import api from "../../setupMiddleware";
import useApiLoader from "../../Utils/useApiLoader";
import Loader from "../Loader/Loader";
import { AnimatePresence, motion } from "framer-motion";
import { ToastContainer, toast, Bounce } from "react-toastify";
import Pagination from "../Common/Pagination";

export default function UserManagement() {
  const loading = useApiLoader();
  const [users, setUsers] = useState([]);

  const [showRecharge, setShowRecharge] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [amount, setAmount] = useState("");
  const [jumpInput, setJumpInput] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 8;

  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    is_admin: false,
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/api/users");
      if (res.status === 200) setUsers(res.data.users);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((u) =>
      `${u.first_name} ${u.last_name} ${u.email} ${u.phone}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [users, search]);

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  const pagesToShow = 5;
  const getVisiblePages = () => {
    if (totalPages <= pagesToShow)
      return [...Array(totalPages).keys()].map((n) => n + 1);

    const left = Math.max(1, page - Math.floor(pagesToShow / 2));
    const right = Math.min(totalPages, left + pagesToShow - 1);

    let pages = [...Array(right - left + 1).keys()].map((n) => left + n);

    if (left > 1) pages.unshift("...");
    if (right < totalPages) pages.push("...");

    if (left > 1) pages.unshift(1);
    if (right < totalPages) pages.push(totalPages);

    return pages;
  };

  const handleJump = (e) => {
    e.preventDefault();
    let pageNum = Number(jumpInput);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      setJumpInput("");
    }
  };

  const handleSubmit = async () => {
    let newErrors = {};

    // Frontend validation
    if (!formData.first_name.trim())
      newErrors.first_name = "First name is required.";
    if (!formData.last_name.trim())
      newErrors.last_name = "Last name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    if (!editingUser && !formData.password.trim())
      newErrors.password = "Password is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let payload = { ...formData };
      payload.name = `${formData.first_name} ${formData.last_name}`.trim();

      let res;
      if (editingUser) {
        delete payload.password;
        payload.user_id = editingUser.id; // ✅ Required for backend validation

        res = await api.post(`/api/admin/users/update/`, payload);
      } else {
        res = await api.post("/api/admin/users/create", payload);
      }

      // Success UI updates
      fetchUsers();
      setShowForm(false);
      setEditingUser(null);
      toast.success(res.data?.message || "User Saved Successfully!", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        is_admin: false,
        password: "",
      });
      setErrors({});
    } catch (error) {
      const backendMsg = error.response?.data?.message;
      const backendErrors = error.response?.data?.errors;

      // If Laravel validation errors exist
      if (backendErrors) {
        const formattedErrors = {};
        Object.keys(backendErrors).forEach((field) => {
          formattedErrors[field] = backendErrors[field][0];
        });
        setErrors(formattedErrors);
        return;
      }

      // Otherwise show full backend message
      toast.error(backendMsg || "Something went wrong!", {
        position: "top-left",
        theme: "colored",
      });
    }
  };

  const handleRecharge = async () => {
    let newErrors = {};
    if (!selectedUser) newErrors.user_id = "Select user";
    if (!amount || Number(amount) <= 0) newErrors.amount = "Invalid amount";

    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    try {
      const res = await api.post("/api/admin/update-balance", {
        user_id: selectedUser,
        amount: Number(amount),
      });
      toast.success(res.data?.message || "User Saved Successfully!", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
      setAmount("");
      setErrors({});
      fetchUsers();
      setShowRecharge(false);
    } catch (error) {
      setErrors(error.response?.data?.errors || {});
      toast.error(error.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.first_name} ${u.last_name}?`)) return;
    await api.delete(`/api/users/${u.id}`);
    fetchUsers();
  };

  return (
    <>
      {loading && <Loader opacity={50} />}

      <div className="bg-white/95 border border-gray-100 rounded-2xl shadow-sm p-6 mt-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-5">
          <h2 className="font-semibold text-gray-800 text-lg">
            User Management
          </h2>

          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search users..."
                className="pl-8 pr-3 py-2 text-sm border rounded-lg bg-white w-full sm:w-60"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              onClick={() => {
                setEditingUser(null);
                setFormData({
                  first_name: "",
                  last_name: "",
                  email: "",
                  phone: "",
                  is_admin: false,
                  password: "",
                });
                setShowForm(true);
              }}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 text-sm"
            >
              <FaUserPlus /> Add User
            </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[800px] text-left border-separate border-spacing-y-2">
            <thead>
              <tr className="text-gray-600 text-sm">
                <th className="py-1 px-2">Name</th>
                <th className="py-1 px-2">Email</th>
                <th className="py-1 px-2">Phone</th>
                <th className="py-1 px-2">Balance</th>
                <th className="py-1 px-2">Last Modified</th>
                <th className="py-1 px-2 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedUsers.map((u) => (
                <motion.tr
                  key={u.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="bg-gray-50 hover:bg-gray-100 transition rounded-xl"
                >
                  <td className="py-2 px-2 font-medium text-xs">
                    {u.first_name} {u.last_name}
                  </td>
                  <td className="py-2 px-2 text-[13px]">{u.email}</td>
                  <td className="py-2 px-2 text-[13px]">{u.phone || "-"}</td>
                  <td className="py-2 px-2 text-[13px]">₹{u.balance || 0}</td>
                  <td className="py-2 px-2 text-[13px]">
                    {moment(u.updated_at).format("DD MMM YYYY, hh:mm A")}
                  </td>
                  <td className="py-2 px-2 text-right flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setSelectedUser(u.id);
                        setShowRecharge(true);
                      }}
                      className="p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
                    >
                      <FaWallet />
                    </button>
                    <button
                      onClick={() => {
                        setEditingUser(u);
                        setFormData({ ...u, password: "" });
                        setShowForm(true);
                      }}
                      className="p-2 bg-brand/10 text-brand rounded-lg hover:bg-brand/20"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      className="p-2 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {paginatedUsers.map((u) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-2xl border border-gray-100 bg-white shadow-[0_2px_6px_rgba(0,0,0,0.06)]"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-gray-900 text-base">
                    {u.first_name} {u.last_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-[1px]">{u.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400">Wallet</p>
                  <p className="text-brand font-bold text-lg leading-none">
                    ₹{u.balance?.toLocaleString("en-IN") || 0}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                <FaPhone className="text-brand/70 rotate-90 text-[12px]" />
                {u.phone || "—"}
              </p>

              <p className="text-[11px] text-gray-400 mt-1">
                Last Modified:{" "}
                {moment(u.updated_at).format("DD MMM YYYY, hh:mm A")}
              </p>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedUser(u.id);
                    setShowRecharge(true);
                  }}
                  className="p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
                >
                  <FaWallet />
                </button>
                <button
                  onClick={() => {
                    setEditingUser(u);
                    setFormData({ ...u, password: "" });
                    setShowForm(true);
                  }}
                  className="p-2 bg-brand/10 text-brand rounded-lg hover:bg-brand/20"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(u)}
                  className="p-2 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200"
                >
                  <FaTrash />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl border border-gray-200 
                   max-h-[90vh] overflow-y-auto no-scrollbar p-8"
            >
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-3 right-4 text-gray-400 hover:text-brand transition text-lg"
              >
                ✕
              </button>

              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-brand/10 rounded-full mb-2">
                  <FaUser className="text-brand text-xl" />
                </div>
                <h2 className="text-xl font-semibold text-brand">
                  {editingUser ? "Edit User" : "Add User"}
                </h2>
              </div>

              <div className="space-y-4 bg-[#f8f9fa] p-5 rounded-2xl border border-gray-200">
                {/* First Name */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    First Name
                  </label>
                  <div className="flex items-center gap-3 border border-gray-300 bg-white rounded-lg px-3 py-2.5">
                    <FaUser className="text-brand/80" />
                    <input
                      type="text"
                      className="flex-1 bg-transparent border-none text-sm"
                      value={formData.first_name}
                      onChange={(e) =>
                        setFormData({ ...formData, first_name: e.target.value })
                      }
                    />
                  </div>
                  {errors.first_name && (
                    <p className="text-xs text-rose-600">{errors.first_name}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Last Name
                  </label>
                  <div className="flex items-center gap-3 border border-gray-300 bg-white rounded-lg px-3 py-2.5">
                    <FaUser className="text-brand/80" />
                    <input
                      type="text"
                      className="flex-1 bg-transparent border-none text-sm"
                      value={formData.last_name}
                      onChange={(e) =>
                        setFormData({ ...formData, last_name: e.target.value })
                      }
                    />
                  </div>
                  {errors.last_name && (
                    <p className="text-xs text-rose-600">{errors.last_name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Email
                  </label>
                  <div className="flex items-center gap-3 border border-gray-300 bg-white rounded-lg px-3 py-2.5">
                    <FaEnvelope className="text-brand/80" />
                    <input
                      type="email"
                      className="flex-1 bg-transparent border-none text-sm"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-rose-600">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Phone
                  </label>
                  <div className="flex items-center gap-3 border border-gray-300 bg-white rounded-lg px-3 py-2.5">
                    <FaPhone className="text-brand/80 rotate-90" />
                    <input
                      type="text"
                      className="flex-1 bg-transparent border-none text-sm"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Password Only for Add */}
                {!editingUser && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Password
                    </label>

                    <div className="flex items-center gap-3 border border-gray-300 bg-white rounded-lg px-3 py-2.5">
                      <FaShieldAlt className="text-brand/80" />
                      <input
                        type={showPassword ? "text" : "password"}
                        className="flex-1 bg-transparent border-none text-sm"
                        placeholder="Password"
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-500 hover:text-brand transition"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    {errors.password && (
                      <p className="text-xs text-rose-600 mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 text-sm"
                >
                  {editingUser ? "Save Changes" : "Add User"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Recharge Modal */}
      <AnimatePresence>
        {showRecharge && (
          <motion.div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm relative">
              <button
                onClick={() => setShowRecharge(false)}
                className="absolute top-3 right-4 text-gray-400 hover:text-brand text-lg"
              >
                ✕
              </button>
              <h2 className="text-lg font-semibold text-brand text-center mb-5">
                Recharge Wallet
              </h2>

              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Amount
              </label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />

              <button
                onClick={handleRecharge}
                className="mt-6 w-full bg-brand text-white py-2.5 rounded-xl hover:bg-brand/90"
              >
                Add Amount
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer />
    </>
  );
}
